import { dictToList } from "@utils/dictUtils";
import { delay } from "@utils/asyncUtils";
import { getFrontendTranslations } from "@newgenerated/shared/stubs/getFrontendTranslations";
import { assert } from "@utils/assertion";
import type { Language } from "@newgenerated/shared/schema";

type TextResolveStrategy = "FALLBACK_TO_KEY" | "UNDEFINED";
type TranslationSimpleFn = (key: string, list?: string[]) => string;
type TranslationUndefinedFn = (key: string, list?: string[], resolveStrategy?: TextResolveStrategy) => string | undefined;
type TranslationWithDefaultMsgFn = (key: string, fallbackKey: string) => string;
type TranslationWithListWithDefaultMsgFn = (key: string, list: string[], fallbackKey: string) => string;
type TranslationResolveToKeyFn = (key: string, resolveStrategy: "FALLBACK_TO_KEY") => string;
type TranslationResolveToUndefinedFn = (key: string, resolveStrategy: "UNDEFINED") => string | undefined;
type TranslationWithListWithResolveToKeyFn = (key: string, list: string[], resolveStrategy: "FALLBACK_TO_KEY") => string;
type TranslationWithListResolveToUndefinedFn = (key: string, list: string[], resolveStrategy: "UNDEFINED") => string | undefined;

export type TranslationFn = TranslationSimpleFn &
  TranslationWithDefaultMsgFn &
  TranslationWithListWithDefaultMsgFn &
  TranslationUndefinedFn &
  TranslationResolveToKeyFn &
  TranslationResolveToUndefinedFn &
  TranslationWithListWithResolveToKeyFn &
  TranslationWithListResolveToUndefinedFn;
type InternalDataState<T> =
  | {
      state: "AVAILABLE";
      value: T;
    }
  | {
      state: "MISSING" | "LOADING";
    };

type InternalData = {
  [Property in Language | "ar"]: InternalDataState<Map<string, string>>;
};

function initialInternalData(): InternalData {
  return {
    en: {
      state: "MISSING",
    },
    de: {
      state: "MISSING",
    },
    es: {
      state: "MISSING",
    },
    ru: {
      state: "MISSING",
    },
    zh: {
      state: "MISSING",
    },
    pt: {
      state: "MISSING",
    },
    fr: {
      state: "MISSING",
    },
    ar: {
      state: "MISSING",
    },
  };
}

const internalData = initialInternalData();

async function loadAsyncData(lang: Language, loader: (lang: Language) => Promise<Map<string, string>>): Promise<Map<string, string>> {
  while (true) {
    const currentStatus = internalData[lang];

    if (currentStatus.state === "AVAILABLE") {
      // Data has already been set up
      return currentStatus.value;
    }

    if (currentStatus.state === "MISSING") {
      // Run loader for the first time
      internalData[lang] = {
        state: "LOADING",
      } satisfies InternalDataState<unknown>;

      const data = await loader(lang);

      internalData[lang] = {
        state: "AVAILABLE",
        value: data,
      } satisfies InternalDataState<unknown>;
      return data;
    }

    // There is a request for the question in process
    await delay(100);
  }
}

function interpolateString(text: string, list: string[]): string {
  return list.reduce((acc, curr, index) => {
    return acc.replace(new RegExp(`\\{${index}\\}`, "g"), curr);
  }, text);
}

async function getGaDesignTranslations(language: Language): Promise<Map<string, string>> {
  return await loadAsyncData(language, async (lang: Language) => {
    const response = await getFrontendTranslations({ frontendProject: "DESIGN", isoCode: lang });
    assert(response.kind === "success");
    return new Map(dictToList(response.data.keys.additionalProperties));
  });
}

export function createTranslationFunction(translations: Map<string, string>): TranslationFn {
  const translationFn: TranslationSimpleFn = (key, list) => {
    const translatedText = translations.get(key);

    if (translatedText !== undefined) {
      return list === undefined ? translatedText : interpolateString(translatedText, list);
    }
    return `???${key}???`;
  };

  const translationUndefinedFn: TranslationUndefinedFn = (key, list) => {
    const translatedText = translations.get(key);
    if (translatedText === undefined || list === undefined) {
      return translatedText;
    }
    return interpolateString(translatedText, list);
  };

  const translationWithDefaultMsgFn: TranslationWithDefaultMsgFn = (key, fallbackKey) => {
    const translatedText = translations.get(key);
    return translatedText ?? translationFn(fallbackKey);
  };

  const translationWithListWithDefaultMsgFn: TranslationWithListWithDefaultMsgFn = (key, list, fallbackKey) => {
    const translatedText = translations.get(key);
    if (translatedText === undefined) {
      return translationFn(fallbackKey, list);
    }
    return interpolateString(translatedText, list);
  };

  return ((key: string, ...args: [] | [string[]] | [string] | [TextResolveStrategy] | [string[], string] | [string[], TextResolveStrategy]) => {
    if (args.length === 0) {
      return translationFn(key);
    } else if (args.length === 1) {
      if (args[0] === "UNDEFINED") {
        return translationUndefinedFn(key);
      } else if (args[0] === "FALLBACK_TO_KEY") {
        return translationFn(key);
      } else if (typeof args[0] === "string") {
        return translationWithDefaultMsgFn(key, args[0]);
      } else {
        return translationFn(key, args[0]);
      }
    } else {
      if (args[1] === "UNDEFINED") {
        return translationUndefinedFn(key, args[0]);
      } else if (args[1] === "FALLBACK_TO_KEY") {
        return translationFn(key, args[0]);
      } else {
        return translationWithListWithDefaultMsgFn(key, args[0], args[1]);
      }
    }
  }) as TranslationFn;
}

export async function getTranslationFunction(lang: Language): Promise<TranslationFn> {
  const translations = await getGaDesignTranslations(lang);
  return createTranslationFunction(translations);
}
